"use client";

import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { guniWheelData, guniWheelXData } from "@/lib/comparison-data";

type ActiveTab = "guni-wheel" | "guni-wheel-x";

const tabs: { label: string; value: ActiveTab }[] = [
  { label: "GUNI WHEEL", value: "guni-wheel" },
  { label: "GUNI WHEEL X", value: "guni-wheel-x" },
];

export default function FitmentComparison() {
  const [activeTab, setActiveTab] = useState<ActiveTab>("guni-wheel");

  const data = useMemo(
    () => (activeTab === "guni-wheel" ? guniWheelData : guniWheelXData),
    [activeTab]
  );

  return (
    <section className="bg-background-50 px-4 sm:px-6 xl:px-10 py-[60px] xl:py-[100px]">
      <div className="w-full max-w-[1440px] mx-auto flex flex-col gap-8 xl:gap-[60px]">

        {/* Heading */}
        <div className="flex flex-col gap-4 xl:gap-[30px] w-full xl:w-[1360px] xl:h-[160px]">
          <h2 className="font-oswald font-medium text-[28px] sm:text-[42px] xl:text-[60px] leading-[1.2] xl:leading-[80px] tracking-[0.2px] uppercase text-neutral-500 xl:h-[80px]">
            COMPARE GUNIWHEEL & GUNIWHEEL X MODELS
          </h2>
          <p className="font-roboto font-normal text-[14px] sm:text-[16px] xl:text-[18px] leading-[25px] text-neutral-300 xl:h-[50px]">
            Quickly compare all Guniwheel™ models side-by-side and find the universal wheel that best fits your vehicle type, workshop needs, and load requirements. This chart provides a clear overview of specifications, capacities, and compatibility to help you choose confidently.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-4 xl:gap-5 w-full xl:w-[620px] xl:h-[60px] mx-auto">
          {tabs.map((tab) => (
            <Button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`w-full xl:w-[300px] h-[60px] px-6 py-[10px] rounded-[10px] font-oswald font-medium text-[16px] xl:text-[20px] leading-[100%] tracking-[0.2px] uppercase transition-colors hover:bg-opacity-100 cursor-pointer shadow-none ${
                activeTab === tab.value
                  ? "bg-primary-500 text-white border-b-2 border-primary-500"
                  : "bg-background text-neutral-500 border-b-2 border-transparent"
              }`}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        {/* White Card Table */}
        <div className="w-full bg-background rounded-[12px] overflow-hidden p-4 xl:p-5 flex flex-col gap-0">

          {/* Header - gray rounded inside white card */}
          <div className="bg-background-50 rounded-[12px] w-full xl:h-[80px] flex items-center justify-between px-4 xl:px-5 py-4 xl:py-6">
            <div className="font-oswald font-bold text-[14px] sm:text-[16px] xl:text-[20px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500 flex-1">
              SPECIFICATION
            </div>
            <div className="xl:w-[492px] gap-4 xl:gap-5 flex">
              {data.columns.map((col) => (
                <div key={col} className="font-oswald font-bold text-[14px] sm:text-[16px] xl:text-[20px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500 flex-1 px-3 whitespace-nowrap flex items-center justify-center">
                  <span className="xl:w-[133px]">{col}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="flex flex-col">
            {data.specifications.map((spec) => (
              <div key={spec.name} className="flex items-center justify-between py-4 xl:py-6 px-4 xl:px-5 w-full xl:h-[80px] border-b border-stroke last:border-b-0">
                <div className="font-oswald font-medium text-[12px] sm:text-[14px] xl:text-[18px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500 flex-1">
                  {spec.name}
                </div>
                <div className="xl:w-[492px] gap-4 xl:gap-5 flex flex-row items-center">
                  {spec.values.map((val, i) => (
                    <div key={i} className="font-roboto font-normal text-[12px] sm:text-[14px] xl:text-[16px] text-neutral-300 text-center flex-1 xl:w-[157px] py-[10px] px-3">
                      {Array.isArray(val) ? (
                        <div className="flex flex-col gap-1">
                          {val.map((item) => (
                            <div key={item}>{item}</div>
                          ))}
                        </div>
                      ) : (
                        val
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}